import { add, dec, div, fromDouble, fromInt, gt, isZero, mul, toDouble, ZERO, type Decimal } from '../core/decimal.ts';
import { powDouble } from '../core/pow.ts';

/** InvestmentGrowth.illustrativeAnnualReturnPercent. */
export const ILLUSTRATIVE_ANNUAL_RETURN_PERCENT = dec(7);

const monthlyRateOf = (annualRatePercent: Decimal): Decimal => div(div(annualRatePercent, dec(100)), dec(12));

/**
 * InvestmentGrowth.contributionBalances: each month contribute, then grow by rate/100/12
 * (an annuity due); the balance after each month.
 */
export function contributionBalances(monthlyContribution: Decimal, annualRatePercent: Decimal, months: number): readonly Decimal[] {
  const monthlyRate = monthlyRateOf(annualRatePercent);
  const balances: Decimal[] = [];
  let balance: Decimal = ZERO;
  for (let month = 0; month < Math.max(0, months); month++) {
    balance = add(balance, monthlyContribution);
    balance = add(balance, mul(balance, monthlyRate));
    balances.push(balance);
  }
  return balances;
}

/** InvestmentGrowth.contributionFutureValue: the last contribution balance, or 0. */
export const contributionFutureValue = (monthlyContribution: Decimal, annualRatePercent: Decimal, months: number): Decimal =>
  contributionBalances(monthlyContribution, annualRatePercent, months).at(-1) ?? ZERO;

/**
 * InvestmentGrowth.annuityFutureValue: ordinary annuity PMT × ((1+r)^n − 1)/r with the factor
 * in Double; with no growth, PMT × n.
 */
export function annuityFutureValue(monthlyPayment: Decimal, annualRatePercent: Decimal, months: number): Decimal {
  const monthlyRate = monthlyRateOf(annualRatePercent);
  if (!gt(monthlyRate, ZERO)) return mul(monthlyPayment, fromInt(months));
  const r = toDouble(monthlyRate);
  const compoundFactor = (powDouble(1 + r, months) - 1) / r;
  return mul(monthlyPayment, fromDouble(compoundFactor));
}

/** InvestmentGrowth.lumpSumFutureValue: principal × (1 + rate/100)^years, multiplied out yearly in Decimal. */
export function lumpSumFutureValue(principal: Decimal, annualRatePercent: Decimal, years: number): Decimal {
  const growth = add(dec(1), div(annualRatePercent, dec(100)));
  const multiplier = Array.from({ length: Math.max(0, years) }).reduce<Decimal>((m) => mul(m, growth), dec(1));
  return mul(principal, multiplier);
}

/**
 * InvestmentGrowth.yearsOfGrowthEquivalent: |amount / (base × rate/100)| as a Double; 0 when
 * amount or base is 0 or base does not grow.
 */
export function yearsOfGrowthEquivalent(amount: Decimal, base: Decimal, annualRatePercent: Decimal): number {
  if (isZero(amount) || isZero(base)) return 0;
  const annualGrowth = mul(base, div(annualRatePercent, dec(100)));
  if (!gt(annualGrowth, ZERO)) return 0;
  return Math.abs(toDouble(div(amount, annualGrowth)));
}

